import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ResponsiveModal from 'components/_UI/ResponsiveModal';
import { updateReservationSettings } from 'api/requests/reservations';
import colors from 'theme/colors';
import { panelAccent } from 'theme/panelAccent';

const MIN_CAPACITY = 1;
const MAX_CAPACITY = 65535;
const DEFAULT_SLOT_INTERVAL = 30;
const DEFAULT_MAX_PARTY_SIZE = 12;
const SLOT_INTERVAL_OPTIONS = [15, 30, 45, 60, 90, 120];

const isValidCount = (raw) => {
    const n = Number(raw);
    return raw !== '' && Number.isInteger(n) && n >= MIN_CAPACITY && n <= MAX_CAPACITY;
};

export default function ReservationSettingsModal({ isOpen, onClose, establishmentSlug, settings, onSuccess, toast }) {
    const [unlimited, setUnlimited] = useState(true);
    const [capacity, setCapacity] = useState('');
    // The max party input shows the effective value (stored, else the fallback). We only send
    // a number if the user edits it, so an untouched `null` stays `null` (keeps following the capacity).
    const [maxParty, setMaxParty] = useState('');
    const [maxPartyTouched, setMaxPartyTouched] = useState(false);
    const [slotInterval, setSlotInterval] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        const current = settings?.max_party_size_per_slot;
        setUnlimited(current == null);
        setCapacity(current != null ? String(current) : '');
        setMaxParty('');
        setMaxPartyTouched(false);
        const currentInterval = settings?.slot_interval_minutes;
        setSlotInterval(currentInterval != null ? String(currentInterval) : '');
    }, [isOpen, settings]);

    // Keep a saved value selectable even if it isn't one of the presets.
    const intervalOptions = [...new Set([...SLOT_INTERVAL_OPTIONS, Number(slotInterval) || DEFAULT_SLOT_INTERVAL])]
        .sort((a, b) => a - b);

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    const isCapacityValid = unlimited || isValidCount(capacity);
    const storedMaxParty = settings?.max_party_size ?? null;
    // Mirrors the backend: null falls back to the per-slot capacity, or 12 if that is also null.
    const fallbackMaxParty = !unlimited && isValidCount(capacity) ? Number(capacity) : DEFAULT_MAX_PARTY_SIZE;
    const maxPartyDisplay = maxPartyTouched ? maxParty : String(storedMaxParty ?? fallbackMaxParty);
    // Empty after editing means "back to automatic" (null).
    const isMaxPartyValid = !maxPartyTouched || maxParty === '' || isValidCount(maxParty);
    const maxPartyPayload = !maxPartyTouched ? storedMaxParty : (maxParty === '' ? null : Number(maxParty));
    const effectiveMaxParty = Math.min(maxPartyPayload ?? Infinity, !unlimited && isValidCount(capacity) ? Number(capacity) : Infinity);
    const isValid = isCapacityValid && isMaxPartyValid;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValid || saving) return;

        try {
            setSaving(true);
            const updated = await updateReservationSettings(establishmentSlug, {
                max_party_size_per_slot: unlimited ? null : Number(capacity),
                max_party_size: maxPartyPayload,
                slot_interval_minutes: slotInterval === '' ? null : Number(slotInterval),
            });
            onSuccess(updated);
            toast.success('Configurações de reserva atualizadas!');
            onClose();
        } catch (error) {
            console.error('Error updating reservation settings', error);
            const message = error?.response?.data?.message || 'Erro ao atualizar as configurações.';
            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <ResponsiveModal isOpen={isOpen} onClose={handleClose} title='Configurações de reserva' size='default'>
            <Form onSubmit={handleSubmit}>
                <Hint>
                    Defina quantas pessoas, no total, podem reservar mesa no mesmo horário. Deixe sem limite para aceitar
                    qualquer quantidade de reservas.
                </Hint>

                <Field>
                    <Label htmlFor='reservation-capacity'>Capacidade máxima por horário (pessoas)</Label>
                    <Input
                        id='reservation-capacity'
                        type='number'
                        min={MIN_CAPACITY}
                        max={MAX_CAPACITY}
                        value={capacity}
                        disabled={unlimited}
                        onChange={e => setCapacity(e.target.value)}
                        placeholder='Ex: 40'
                    />
                </Field>

                <CheckboxField>
                    <input
                        id='reservation-unlimited'
                        type='checkbox'
                        checked={unlimited}
                        onChange={e => setUnlimited(e.target.checked)}
                    />
                    <label htmlFor='reservation-unlimited'>Sem limite</label>
                </CheckboxField>

                <Field>
                    <Label htmlFor='reservation-max-party'>Maior grupo por reserva (pessoas)</Label>
                    <Input
                        id='reservation-max-party'
                        type='number'
                        min={MIN_CAPACITY}
                        max={MAX_CAPACITY}
                        value={maxPartyDisplay}
                        onChange={e => { setMaxPartyTouched(true); setMaxParty(e.target.value); }}
                        placeholder={String(fallbackMaxParty)}
                    />
                    <FieldHint>
                        Grupos maiores que isso não veem horários disponíveis. Não é um limite diário nem por horário.
                        {Number.isFinite(effectiveMaxParty) && effectiveMaxParty < Number(maxPartyDisplay)
                            ? ` Na prática o limite é ${effectiveMaxParty}, pois a capacidade por horário é menor.`
                            : ''}
                    </FieldHint>
                </Field>

                <Field>
                    <Label htmlFor='reservation-slot-interval'>Intervalo entre horários</Label>
                    <Select
                        id='reservation-slot-interval'
                        value={slotInterval}
                        onChange={e => setSlotInterval(e.target.value)}
                    >
                        <option value=''>Padrão ({DEFAULT_SLOT_INTERVAL} min)</option>
                        {intervalOptions.map(m => (
                            <option key={m} value={m}>{m} min</option>
                        ))}
                    </Select>
                    <FieldHint>
                        Ex: com funcionamento das 12:00 às 14:00 e intervalo de 30 min, o cliente vê 12:00, 12:30, 13:00 e 13:30.
                    </FieldHint>
                </Field>

                <SubmitButton type='submit' disabled={!isValid || saving}>
                    {saving ? 'Salvando...' : 'Salvar'}
                </SubmitButton>
            </Form>
        </ResponsiveModal>
    );
}

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 4px 2px 8px;
`;

const Hint = styled.p`
    font-size: 13px;
    color: #666;
    margin: 0;
    line-height: 1.4;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Label = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Input = styled.input`
    width: 100%;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    padding: 11px 12px;
    font-size: 14px;
    font-family: inherit;
    color: ${colors.nightDark};
    background: #fff;

    &:disabled { background: ${colors.neutralBackground}; color: #999; }
    &:focus { outline: none; border-color: ${panelAccent(colors.occasionalPurple)}; }
`;

const FieldHint = styled.span`
    font-size: 12px;
    color: #666;
    line-height: 1.4;
`;

const Select = styled.select`
    width: 100%;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    padding: 11px 12px;
    font-size: 14px;
    font-family: inherit;
    color: ${colors.nightDark};
    background: #fff;

    &:focus { outline: none; border-color: ${panelAccent(colors.occasionalPurple)}; }
`;

const CheckboxField = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${colors.nightDark};

    input { width: 16px; height: 16px; cursor: pointer; }
    label { cursor: pointer; }
`;

const SubmitButton = styled.button`
    margin-top: 4px;
    height: 48px;
    border-radius: 999px;
    border: none;
    background: ${panelAccent(colors.occasionalPurple)};
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover:not(:disabled) { opacity: 0.92; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
